// Override picker: shows top-N suggestions and a search-as-you-type input.
// Renders inline (full-pane overlay) — no portal, since the taskpane IS
// the pane. Closes via onSelect (picked case) or onCancel.

import { useEffect, useState } from "react";
import {
  fetchCandidates,
  searchCases,
  sourceLabel,
  type CandidateCase,
  type SearchResultCase,
  type MatchInput,
} from "../../lib/cases";

interface CasePickerProps {
  matchInput: MatchInput;
  onSelect: (selected: CandidateCase | SearchResultCase) => void;
  onCancel: () => void;
}

export function CasePicker({ matchInput, onSelect, onCancel }: CasePickerProps) {
  const [candidates, setCandidates] = useState<CandidateCase[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResultCase[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial: fetch top-N candidates for this email
  useEffect(() => {
    const ac = new AbortController();
    setCandidatesLoading(true);
    fetchCandidates(matchInput, 5, ac.signal)
      .then((r) => setCandidates(r.candidates))
      .catch((e) => {
        if ((e as { name?: string }).name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Kon kandidaten niet laden");
      })
      .finally(() => setCandidatesLoading(false));
    return () => ac.abort();
  }, [matchInput]);

  // Search-as-you-type, debounced ~250ms
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const ac = new AbortController();
    const t = setTimeout(() => {
      searchCases(trimmed, ac.signal)
        .then((r) => setSearchResults(r.cases))
        .catch((e) => {
          if ((e as { name?: string }).name === "AbortError") return;
          setError(e instanceof Error ? e.message : "Zoeken mislukt");
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [query]);

  return (
    <div className="absolute inset-0 bg-chrome-bg flex flex-col">
      <header className="px-4 py-3 border-b border-chrome-border flex items-center gap-2">
        <h2 className="text-sm font-semibold flex-1">Kies dossier</h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-chrome-subtle hover:text-chrome-text"
        >
          Annuleer
        </button>
      </header>

      <div className="px-4 py-3 border-b border-chrome-border">
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek op dossier-naam of -nummer…"
          className="w-full px-3 py-2 text-sm rounded border border-chrome-border focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
        />
      </div>

      <main className="flex-1 overflow-y-auto p-4 space-y-4">
        {error && <p className="text-xs text-red-600">{error}</p>}

        {query.trim().length >= 2 ? (
          <section className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-chrome-subtle">Zoekresultaten</h3>
            {searching && <p className="text-xs text-chrome-subtle">Zoeken…</p>}
            {!searching && searchResults.length === 0 && (
              <p className="text-xs text-chrome-subtle">Geen dossiers gevonden voor "{query.trim()}".</p>
            )}
            {searchResults.map((c) => (
              <CaseRow key={c.caseId} title={c.caseTitle} matter={c.matterNumber} onClick={() => onSelect(c)} />
            ))}
          </section>
        ) : (
          <section className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-chrome-subtle">Suggesties voor deze mail</h3>
            {candidatesLoading && <p className="text-xs text-chrome-subtle">Laden…</p>}
            {!candidatesLoading && candidates.length === 0 && (
              <p className="text-xs text-chrome-subtle">Geen suggesties — gebruik zoeken hierboven.</p>
            )}
            {candidates.map((c) => (
              <CaseRow
                key={c.caseId}
                title={c.caseTitle}
                matter={c.matterNumber}
                badge={sourceLabel(c.primarySource)}
                onClick={() => onSelect(c)}
              />
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

interface CaseRowProps {
  title: string;
  matter: string | null;
  badge?: string;
  onClick: () => void;
}

function CaseRow({ title, matter, badge, onClick }: CaseRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded border border-chrome-border bg-white p-3 hover:border-accent transition-colors flex items-start gap-2"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        <div className="text-xs text-chrome-subtle truncate">{matter ?? "— geen dossiernummer"}</div>
      </div>
      {badge && (
        <span className="shrink-0 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">
          {badge}
        </span>
      )}
    </button>
  );
}
